import type {
  ProductVariant,
  DiscountCodeInfo,
  CustomQuery,
  Cart,
  LineItem,
  MyCartUpdateAction,
} from '~~/types/commercetools';
import type { MeWithCartFallback } from '~~/types/commercetools/meWithCartFallback';

import { CartState } from '~~/types/commercetools';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { useCheckoutQueryStore } from '~/stores/checkout/useCheckoutQueryStore';
import type { CountryCodeValue } from '~~/types/localization/localizationHelperTypes';
import { isCheckoutDisabled } from '~~/helpers/Checkout/isCheckoutDisabled';
import { Logger } from '~~/helpers/logger';
import { setCountryAction } from '~~/helpers/cart/cartActions';
import { CUSTOM_QUERIES } from '~~/constants/customQueries';

type CartResponse = {
  cart: Cart;
}

export function useCartApi(countryCode: CountryCodeValue) {

  const currentCountryCode = countryCode;
  const { makeCommercetoolsRequest } = useExpondoMiddleware();
  const checkoutQueryStore = useCheckoutQueryStore();

  const resolveCartIdFromContext = (): string => {

    const cartIdFromStore = checkoutQueryStore.cartIdFromLink || '';

    if (cartIdFromStore) {

      return cartIdFromStore;

    }

    const route = useRoute();
    const cartIdFromRoute = (route?.query?.cartId as string) || '';

    if (cartIdFromRoute) {

      return cartIdFromRoute;

    }

    if (import.meta.client) {

      const cartIdFromWindow = new URLSearchParams(window.location.search).get('cartId') || '';

      if (cartIdFromWindow) {

        return cartIdFromWindow;

      }

    }

    const cartIdFromRequestUrl = useRequestURL().searchParams.get('cartId') || '';

    return cartIdFromRequestUrl;

  };

  const healCartWithoutCountry = async (cart: Cart): Promise<Cart> => {

    if (cart.cartState !== CartState.Active || cart.country) {

      return cart;

    }

    try {

      const response = await makeCommercetoolsRequest<CartResponse>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.UPDATE_CART,
        data: [
          { id: cart.id, version: cart.version, actions: [setCountryAction(currentCountryCode)] },
          CUSTOM_QUERIES.UPDATE_CART_CUSTOM,
        ],
      });

      return response?.data?.cart || cart;

    } catch (error) {

      Logger.error(`[useCartApi] Failed to set country on cart ${cart.id}:`, error);

      return cart;

    }

  };

  const load = async ({ customQuery }: { customQuery?: CustomQuery } = { customQuery: undefined }) => {

    const cartIdFromLink = resolveCartIdFromContext();

    if (cartIdFromLink) {

      try {

        const cartResponse = await makeCommercetoolsRequest<{ me?: { carts?: { results?: Cart[] } } }>({
          endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_MY_FROZEN_CART,
          data: [{ cartId: cartIdFromLink, customQuery }],
        });

        const loadedCart = cartResponse?.data?.me?.carts?.results?.[0];

        return loadedCart || null;

      } catch (error) {

        Logger.error('[useCartApi] Failed to load frozen cart from cartId:', error);

        return null;

      }

    }

    const response = await makeCommercetoolsRequest<{ me: MeWithCartFallback }>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_ME,
      data: [
        { customer: false }, customQuery,
      ],
    });

    const resolvedCart = response?.data?.me?.activeCart
      || response?.data?.me?.carts?.results?.[0]
      || null;

    if (resolvedCart?.cartState === CartState.Frozen) {

      // The customer's only cart is Frozen (e.g. left over from a "generate quote" flow that was
      // never completed) - leave it untouched so its quote link stays valid, and give them a fresh
      // active cart to keep shopping instead of getting stuck viewing a read-only frozen cart forever.
      const createCartResponse = await makeCommercetoolsRequest<CartResponse>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CREATE_CART,
        data: [{}, customQuery],
      });

      return createCartResponse?.data?.cart || null;

    }

    return resolvedCart ? healCartWithoutCountry(resolvedCart) : null;

  };

  const replicateCart = async (parentOrderNumber: string): Promise<Cart> => {

    const response = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.REPLICATE_CART,
      data: [
        {
          parentOrderNumber,
        },
      ],
    });

    const cart = response?.data?.cart;

    if (!cart) {

      throw new Error('Failed to replicate cart');

    }

    return cart;

  };

  const addItem = async ({
    currentCart,
    product,
    quantity,
    supplyChannel,
    distributionChannel,
    customQuery,
  }: {
    currentCart: Cart;
    product: ProductVariant;
    quantity: number;
    supplyChannel?: string;
    distributionChannel?: string;
    customQuery?: CustomQuery;
  }): Promise<Maybe<Cart>> => {

    let cart: Maybe<Cart> = currentCart;

    if (isCheckoutDisabled(currentCountryCode)) {

      return cart;

    }

    if (!currentCart) {

      const createCartResponse = await makeCommercetoolsRequest<CartResponse>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CREATE_CART,
        data: [{}, currentCart],
      });
      cart = createCartResponse?.data?.cart;

    }

    if (!cart) {

      throw new Error('Cart not found');

    }
    const addToCartResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.ADD_TO_CART,
      data: [
        { id: cart.id, version: cart.version },
        { product, quantity, supplyChannel, distributionChannel },
        customQuery,
      ],
    });

    return addToCartResponse?.data?.cart;

  };

  const removeItem = async ({
    currentCart,
    product,
    customQuery,
  }: { currentCart: Cart; product: LineItem; customQuery?: CustomQuery; }) => {

    const removeFromCartResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.REMOVE_FROM_CART,
      data: [
        {
          id: currentCart.id,
          version: currentCart.version,
        },
        product,
        customQuery,
      ],
    });

    return removeFromCartResponse?.data?.cart;

  };

  const updateItemQty = async ({
    currentCart,
    product,
    quantity,
    customQuery,
  }: {
    currentCart?: Cart;
    product: LineItem;
    quantity?: number;
    customQuery?: CustomQuery;
}) => {

    const updateCartQuantityResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.UPDATE_CART_QUANTITY,
      data: [
        {
          id: currentCart?.id,
          version: currentCart?.version,
        },
        { ...product, quantity },
        customQuery,
      ],
    });

    return updateCartQuantityResponse?.data?.cart;

  };

  const updateCart = async ({
    id,
    version,
    actions,
    customQuery,
  }: {
    id: string;
    version: string;
    actions: MyCartUpdateAction[];
    customQuery?: CustomQuery;
}) => {

    const updateCartQuantityResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.UPDATE_CART,
      data: [
        {
          id,
          version,
          actions,
        },
        customQuery,
      ],
    });

    return updateCartQuantityResponse?.data?.cart;

  };

  const buildDeleteCartPayload = (currentCart: Pick<Cart, 'id' | 'version'>) => [
    {
      id: currentCart.id,
      version: currentCart.version,
    },
  ];

  const clear = async ({ currentCart }: { currentCart: Cart }) => {

    const deleteCartResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.DELETE_CART,
      data: buildDeleteCartPayload(currentCart),
    });

    return deleteCartResponse?.data?.cart;

  };

  const applyCoupon = async ({
    currentCart,
    couponCode,
    customQuery,
  }: {
    currentCart: Cart;
    couponCode: string;
    customQuery?: CustomQuery;
  }) => {

    const applyCartCouponResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.APPLY_CART_COUPON,
      data: [
        {
          id: currentCart.id,
          version: currentCart.version,
        },
        couponCode,
        customQuery,
      ],
    });

    return {
      updatedCart: applyCartCouponResponse?.data?.cart,
      updatedCoupon: couponCode,
    };

  };

  const removeCoupon = async ({
    currentCart,
    couponCode,
    customQuery,
  }: { currentCart: Cart; couponCode: string; customQuery?: CustomQuery }) => {

    const couponId = currentCart?.discountCodes?.find(
      (d: DiscountCodeInfo) => d?.discountCode?.code === couponCode,
    )?.discountCode?.id;

    if (!couponId) {

      return { updatedCart: currentCart };

    }
    const removeCartCouponResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.REMOVE_CART_COUPON,
      data: [
        {
          id: currentCart.id,
          version: currentCart.version,
        },
        { id: couponId, typeId: 'discount-code' },
        customQuery,
      ],
    });

    return { updatedCart: removeCartCouponResponse?.data?.cart };

  };

  return {
    load,
    addItem,
    removeItem,
    updateItemQty,
    clear,
    applyCoupon,
    removeCoupon,
    updateCart,
    replicateCart,
    buildDeleteCartPayload,
  };

}
